import { createApi } from '@reduxjs/toolkit/query/react'
import { AppServices } from 'middleware/services'
import { createQueryResponseHandling } from 'utils/errorHandler'
import { authenticatedHttpServiceQuery } from 'utils/reduxQuery'
import { getRequired } from 'config'
import { DiscountCodeBEForm } from './types'
import { showErrorMessage } from 'uiComponents/message'
import { DiscountPool } from './types'

export const discountsApi = createApi({
    reducerPath: 'discounts',
    tagTypes: ['listDiscountPools', 'getAllDiscountPools', 'listDiscountCodes', 'getDiscountUpload', 'getDiscountCode'],
    baseQuery: authenticatedHttpServiceQuery({
        baseUrl: getRequired('backoffice-endpoint'),
    }),
    endpoints: (builder) => ({}),
})

const extendedDiscountsApi = discountsApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
        listDiscountPools: builder.query({
            providesTags: ['listDiscountPools'],
            queryFn: async ({ accountSlug, query }: { accountSlug: string; query?: string }, api) => {
                try {
                    const data = await AppServices.discountsService.getDiscountPools({ accountSlug, query })
                    return { data }
                } catch (error) {
                    showErrorMessage('Failed to get discount pools', 'listDiscountPools_error')

                    return { error }
                }
            },
        }),
        getAllDiscountPools: builder.query<DiscountPool[], { accountSlug: string; query?: string }>({
            providesTags: (_, __, { accountSlug }) => [
                { type: 'getAllDiscountPools', id: accountSlug },
                'getAllDiscountPools',
            ],
            queryFn: async ({ accountSlug, query }) => {
                try {
                    let allPools: DiscountPool[] = []
                    let currentPage = 1
                    let hasMorePages = true

                    while (hasMorePages) {
                        const pageQuery = `page=${currentPage}&page_size=100`
                        const fullQuery = query ? `${query}&${pageQuery}` : pageQuery

                        const response = await AppServices.discountsService.getDiscountPools({
                            accountSlug,
                            query: fullQuery,
                        })

                        if (response?.entries) {
                            allPools = allPools.concat(response.entries)
                        }

                        if (!response?.nextPage) {
                            hasMorePages = false
                        } else {
                            currentPage = response.nextPage
                        }
                    }

                    return { data: allPools }
                } catch (error) {
                    showErrorMessage('Failed to get all discount pools', 'getAllDiscountPools_error')
                    return { error }
                }
            },
        }),
        listDiscountCodes: builder.query({
            providesTags: ['listDiscountCodes'],
            queryFn: async ({ accountSlug, query }: { accountSlug: string; query?: string }, api) => {
                try {
                    const data = await AppServices.discountsService.getDiscountCodes({ accountSlug, query })

                    return { data }
                } catch (error) {
                    showErrorMessage('Failed to get discount codes', 'listDiscountCodes_error')

                    return { error }
                }
            },
        }),
        listDiscountCodesCursorPaginated: builder.query({
            providesTags: ['listDiscountCodes'],
            queryFn: async ({ accountSlug, query }: { accountSlug: string; query?: string }, api) => {
                try {
                    const data = await AppServices.discountsService.getDiscountCodesCursorPaginated({
                        accountSlug,
                        query,
                    })

                    return { data }
                } catch (error) {
                    showErrorMessage('Failed to get discount codes', 'listDiscountCodes_error')

                    return { error }
                }
            },
        }),
        addPool: builder.mutation({
            invalidatesTags: ['listDiscountPools'],
            queryFn: async ({ accountSlug, name }: { accountSlug: string; name: string }) => {
                try {
                    const data = await AppServices.discountsService.addDiscountPool({ accountSlug, name })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
        addCodeByForm: builder.mutation({
            invalidatesTags: ['listDiscountPools'],
            queryFn: async ({
                accountSlug,
                formData,
                poolUuid,
            }: {
                accountSlug: string
                poolUuid: string
                formData: DiscountCodeBEForm
            }) => {
                try {
                    const data = await AppServices.discountsService.addCodeByForm({
                        accountSlug,
                        formData,
                        poolUuid,
                    })

                    return { data }
                } catch (error) {
                    return { error: error.response }
                }
            },
        }),
        uploadDiscountCode: builder.mutation({
            invalidatesTags: ['listDiscountPools'],
            queryFn: async ({ accountSlug, file, poolUuid }: { accountSlug: string; poolUuid: string; file: File }) => {
                return createQueryResponseHandling(() =>
                    AppServices.discountsService.uploadDiscountCode({
                        accountSlug,
                        file,
                        poolUuid,
                    }),
                )
            },
        }),
        deleteDiscountPool: builder.mutation({
            invalidatesTags: ['listDiscountPools'],
            queryFn: async ({ accountSlug, uuid }: { accountSlug: string; uuid: string }) => {
                try {
                    const data = await AppServices.discountsService.deleteDiscountPool({
                        accountSlug,
                        uuid,
                    })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
        editDiscountPool: builder.mutation({
            invalidatesTags: ['listDiscountPools'],
            queryFn: async ({ accountSlug, uuid, name }: { accountSlug: string; uuid: string; name: string }) => {
                try {
                    const data = await AppServices.discountsService.editDiscountPool({
                        accountSlug,
                        uuid,
                        name,
                    })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
        getDiscountUploadInfo: builder.query({
            providesTags: ['getDiscountUpload'],
            extraOptions: {},
            queryFn: async ({ accountSlug, id }: { accountSlug: string; id: string }) => {
                try {
                    const data = await AppServices.discountsService.getDiscountUploadDetails({
                        accountSlug,
                        id,
                    })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
        getDiscountCodeDetails: builder.query({
            providesTags: (_, __, { codeUuid }) => [{ type: 'getDiscountCode', id: codeUuid }],
            queryFn: async ({
                accountSlug,
                poolUuid,
                codeUuid,
            }: {
                accountSlug: string
                poolUuid: string
                codeUuid: string
            }) => {
                try {
                    const data = await AppServices.discountsService.getDiscountCodeDetails({
                        accountSlug,
                        poolUuid,
                        codeUuid,
                    })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
        editDiscountCode: builder.mutation({
            invalidatesTags: (_, __, { codeUuid }) => ['listDiscountPools', { type: 'getDiscountCode', id: codeUuid }],
            queryFn: async ({
                accountSlug,
                poolUuid,
                codeUuid,
                formData,
            }: {
                accountSlug: string
                poolUuid: string
                codeUuid: string
                formData: DiscountCodeBEForm
            }) => {
                try {
                    const data = await AppServices.discountsService.editDiscountCode({
                        accountSlug,
                        poolUuid,
                        codeUuid,
                        formData,
                    })

                    return { data }
                } catch (error) {
                    return { error: error.response }
                }
            },
        }),
        redeemUnusedDiscountCodesFromUploads: builder.mutation({
            invalidatesTags: ['listDiscountPools'],
            queryFn: async ({ accountSlug, uuid }: { accountSlug: string; uuid: string }) => {
                try {
                    const data = await AppServices.discountsService.redeemUnusedDiscountCodesFromUploads({
                        accountSlug,
                        uuid,
                    })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
        redeemUnusedDiscountCodesFromCode: builder.mutation({
            invalidatesTags: (_, __, { uuid }) => ['listDiscountPools', { type: 'getDiscountCode', id: uuid }],
            queryFn: async ({ accountSlug, uuid }: { accountSlug: string; uuid: string }) => {
                try {
                    const data = await AppServices.discountsService.redeemUnusedDiscountCodesFromCode({
                        accountSlug,
                        uuid,
                    })

                    return { data }
                } catch (error) {
                    return { error }
                }
            },
        }),
    }),
})

export const {
    useListDiscountPoolsQuery,
    useLazyListDiscountPoolsQuery,
    useGetAllDiscountPoolsQuery,
    useAddPoolMutation,
    useLazyListDiscountCodesQuery,
    useLazyListDiscountCodesCursorPaginatedQuery,
    useAddCodeByFormMutation,
    useUploadDiscountCodeMutation,
    useDeleteDiscountPoolMutation,
    useEditDiscountPoolMutation,
    useGetDiscountCodeDetailsQuery,
    useEditDiscountCodeMutation,
    useRedeemUnusedDiscountCodesFromUploadsMutation,
    useRedeemUnusedDiscountCodesFromCodeMutation,
    endpoints,
    util,
} = extendedDiscountsApi

export const { reducer, reducerPath } = discountsApi
